import { useState, useCallback, useEffect } from 'react';
import { get, post, put, del } from 'services/request';
import { KbTree, KbArticle, KbVersion, SelectedNode } from '../types';

export interface OpenTab {
  id: number;
  title: string;
}

export const useKnowledgeBase = () => {
  const [tree, setTree] = useState<KbTree>({ folders: [], articles: [] });
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);
  const [article, setArticle] = useState<KbArticle | null>(null);
  const [articleLoading, setArticleLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [versions, setVersions] = useState<KbVersion[]>([]);
  const [showVersions, setShowVersions] = useState(false);

  // Tabs
  const [openTabs, setOpenTabs] = useState<OpenTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<number | null>(null);

  const fetchTree = useCallback(async () => {
    setLoading(true);
    const data = await get<KbTree>('kb/tree');
    if (data && data.folders !== undefined) {
      setTree(data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  const selectArticle = useCallback(async (id: number) => {
    setSelectedNode({ type: 'article', id });
    setEditing(false);
    setShowVersions(false);
    setActiveTabId(id);

    // Add tab if not already open
    setOpenTabs((prev) => {
      if (prev.some((t) => t.id === id)) return prev;
      return [...prev, { id, title: '...' }];
    });

    setArticleLoading(true);
    const data = await get<KbArticle>(`kb/articles/${id}`);
    if (data && data.id) {
      setArticle(data);
      // Update tab title
      setOpenTabs((prev) => prev.map((t) => (t.id === id ? { ...t, title: data.title } : t)));
    }
    setArticleLoading(false);
  }, []);

  const switchTab = useCallback(async (id: number) => {
    setActiveTabId(id);
    setSelectedNode({ type: 'article', id });
    setEditing(false);
    setShowVersions(false);
    setArticleLoading(true);
    const data = await get<KbArticle>(`kb/articles/${id}`);
    if (data && data.id) {
      setArticle(data);
    }
    setArticleLoading(false);
  }, []);

  const closeTab = useCallback((id: number) => {
    setOpenTabs((prev) => {
      const next = prev.filter((t) => t.id !== id);
      if (activeTabId === id) {
        // Switch to adjacent tab or clear
        const idx = prev.findIndex((t) => t.id === id);
        const newActive = next[Math.min(idx, next.length - 1)];
        if (newActive) {
          // Use setTimeout to avoid state update during render
          setTimeout(() => switchTab(newActive.id), 0);
        } else {
          setActiveTabId(null);
          setArticle(null);
          setSelectedNode(null);
        }
      }
      return next;
    });
  }, [activeTabId, switchTab]);

  const selectFolder = useCallback((id: number) => {
    setSelectedNode({ type: 'folder', id });
    setEditing(false);
    setShowVersions(false);
  }, []);

  const createFolder = useCallback(async (name: string, parentId: number | null) => {
    const result = await post<{ id: number }>('kb/folders', { name, parentId });
    await fetchTree();
    return result;
  }, [fetchTree]);

  const renameFolder = useCallback(async (id: number, name: string) => {
    const result = await put('kb/folders/' + id, { name });
    await fetchTree();
    return result;
  }, [fetchTree]);

  const deleteFolder = useCallback(async (id: number) => {
    const result = await del('kb/folders/' + id);
    await fetchTree();
    if (selectedNode?.type === 'folder' && selectedNode.id === id) {
      setSelectedNode(null);
    }
    return result;
  }, [fetchTree, selectedNode]);

  const moveFolder = useCallback(async (id: number, parentId: number | null) => {
    const result = await put('kb/folders/' + id, { parentId });
    await fetchTree();
    return result;
  }, [fetchTree]);

  const createArticle = useCallback(async (title: string, folderId: number | null) => {
    const result = await post<KbArticle>('kb/articles', { title, content: '', folderId });
    await fetchTree();
    if (result.data && (result.data as any).id) {
      const created = result.data as KbArticle;
      selectArticle(created.id);
      setEditing(true);
    }
    return result;
  }, [fetchTree, selectArticle]);

  const updateArticle = useCallback(async (id: number, title: string, content: string) => {
    const result = await put<KbArticle>('kb/articles/' + id, { title, content });
    if (result.data) {
      const updated = result.data as KbArticle;
      setArticle(updated);
      setOpenTabs((prev) => prev.map((t) => (t.id === id ? { ...t, title: updated.title } : t)));
    }
    await fetchTree();
    setEditing(false);
    return result;
  }, [fetchTree]);

  const deleteArticle = useCallback(async (id: number) => {
    const result = await del('kb/articles/' + id);
    await fetchTree();
    // Close tab if open
    setOpenTabs((prev) => {
      const next = prev.filter((t) => t.id !== id);
      if (activeTabId === id) {
        const newActive = next[0];
        if (newActive) {
          setTimeout(() => switchTab(newActive.id), 0);
        } else {
          setActiveTabId(null);
          setArticle(null);
          setSelectedNode(null);
        }
      }
      return next;
    });
    return result;
  }, [fetchTree, activeTabId, switchTab]);

  const moveArticle = useCallback(async (id: number, folderId: number | null) => {
    const result = await put('kb/articles/' + id + '/move', { folderId });
    await fetchTree();
    return result;
  }, [fetchTree]);

  const fetchVersions = useCallback(async (articleId: number) => {
    const data = await get<KbVersion[]>(`kb/articles/${articleId}/versions`);
    if (Array.isArray(data)) {
      setVersions(data);
    }
    setShowVersions(true);
  }, []);

  const fetchVersionContent = useCallback(async (articleId: number, versionId: number) => {
    return get<KbVersion>(`kb/articles/${articleId}/versions/${versionId}`);
  }, []);

  const restoreVersion = useCallback(async (articleId: number, versionId: number) => {
    const result = await put<KbArticle>(`kb/articles/${articleId}/versions/${versionId}/restore`, {});
    if (result.data) {
      const restored = result.data as KbArticle;
      setArticle(restored);
      setOpenTabs((prev) => prev.map((t) => (t.id === articleId ? { ...t, title: restored.title } : t)));
      await fetchTree();
      await fetchVersions(articleId);
    }
    return result;
  }, [fetchTree, fetchVersions]);

  return {
    tree,
    loading,
    selectedNode,
    article,
    articleLoading,
    editing,
    versions,
    showVersions,
    openTabs,
    activeTabId,
    setEditing,
    setShowVersions,
    selectArticle,
    switchTab,
    closeTab,
    selectFolder,
    createFolder,
    renameFolder,
    deleteFolder,
    moveFolder,
    createArticle,
    updateArticle,
    deleteArticle,
    moveArticle,
    fetchVersions,
    fetchVersionContent,
    restoreVersion,
    fetchTree,
  };
};
